<script lang="ts">
    import { createEventDispatcher } from "svelte"
    import {
        highlighters,
        selectedHighlighter,
        changeSelected,
        getAllHighlights,
        findHighlight,
        type HighlightID,
        Highlight,
    } from "@store/highlights"
    import Icon from "@components/material/Icon.svelte"
    import { open, location } from "./HighlightExpanderOverlay.svelte"
    import { darkmode } from "@utils/material/darkmode"
    import PopupWarning from "@components/popup/PopupWarning.svelte"
    import Popup from "@components/popup/Popup.svelte"
    import Button from "@components/button/Button.svelte"
    const dispatch = createEventDispatcher()

    // Workaround for the weird bug (see parent component)
    // export let highlightIDStore: Writable<string>
    // export let highlightID : HighlightID | null
    export let highlight: Highlight | null

    // Touch script
    let timeout = setTimeout(() => {}, 0)
    let mainColorElement: HTMLElement

    function openExpander() {
        $open = true
        $location = mainColorElement.getBoundingClientRect()
    }

    let pointerdown = false
    let originalEvent: PointerEvent | null = null
    function pointerstart(e: PointerEvent) {
        pointerdown = true
        originalEvent = e

        // e.preventDefault()
        e.stopPropagation()
        timeout = setTimeout(openExpander, 400)
    }

    function pointermove(e: PointerEvent) {
        if (!pointerdown) return
        e.preventDefault()
        e.stopPropagation()

        // Threshold blocker
        if (originalEvent) {
            const hyp = Math.hypot(
                Math.abs(e.clientX - originalEvent.clientX),
                Math.abs(e.clientY - originalEvent.clientY),
            )
            if (hyp > 4) {
                originalEvent = null
            } else {
                return
            }
        }

        clearTimeout(timeout)
        openExpander()

        const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLSpanElement

        if ("colorid" in target.dataset) {
            if (target.dataset.colorid === undefined) throw new Error(`Colorset was undefined`)
            changeSelected(target.dataset.colorid)
        }
    }

    function pointerend(e: PointerEvent) {
        if (!pointerdown) return
        pointerdown = false
        if (e.cancelable) e.preventDefault()
        if (!$open) {
            clearTimeout(timeout)
            if (highlight) {
                if (highlight.hasNote) {
                    return (deleteHighlightWarningOpen = true)
                } else {
                    return dispatchDelete()
                }
            }
        }
        $open = false
        dispatch("highlight")
    }

    function dispatchDelete() {
        dispatch("deletehighlight")
        deleteHighlightWarningOpen = false
        $open = false
    }

    function touchmove(e: TouchEvent) {
        if (pointerdown) e.preventDefault()
    }

    $: color = (highlight?.getHighlighter() || $highlighters.get($selectedHighlighter))?.[
        $darkmode ? "dark" : "light"
    ]

    let deleteHighlightWarningOpen = false
</script>

<svelte:window
    on:pointermove={pointermove}
    on:pointerup={pointerend}
    on:pointercancel={pointerend}
/>
<div
    class="highlightSelector"
    class:delete={!!highlight}
    on:pointerdown={pointerstart}
    on:touchmove|nonpassive={touchmove}
>
    <div>
        <div
            bind:this={mainColorElement}
            class="highlightSelectorBubble mainColor relative bg-white dark:bg-black"
        >
            <div class="highlightSelectorBubble z-0 absolute inset-0" style:background={color} />
            <Icon icon="close-sm" />
        </div>
    </div>
</div>
{#if deleteHighlightWarningOpen}
    <Popup on:exit={() => (deleteHighlightWarningOpen = false)}>
        <PopupWarning>
            <div slot="title">Are you sure you want to delete this highlight?</div>
            <div slot="paragraph">This highlight has a note attached to it.</div>
            <Button color="danger" go={dispatchDelete}>Delete</Button>
            <Button color="white" go={() => (deleteHighlightWarningOpen = false)}>Cancel</Button>
        </PopupWarning>
    </Popup>
{/if}
